import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRepoPreview, type RepoFile, type RepoFileStatus } from '@/hooks/useRepoPreview'
import { useSyncBook, useSyncStatus } from '@/hooks/useSyncBook'

interface Props {
  slug: string
  onClose: () => void
}

const STATUS_META: Record<RepoFileStatus, { dot: string; label: string; row: string }> = {
  synced: { dot: 'bg-emerald-500', label: 'Synced', row: 'text-slate-800' },
  pending: { dot: 'bg-amber-500', label: 'Not pushed', row: 'text-slate-800' },
  pull_pending: { dot: 'bg-sky-500', label: 'Not pulled', row: 'text-slate-800' },
  muted: { dot: 'bg-slate-300', label: 'Ignored', row: 'text-slate-400 line-through decoration-slate-300' },
}

// Hierarchical tree built from the flat file list.
interface DirNode {
  name: string
  path: string
  dirs: Map<string, DirNode>
  files: RepoFile[]
}

function buildTree(files: RepoFile[]): DirNode {
  const root: DirNode = { name: '', path: '', dirs: new Map(), files: [] }
  for (const file of files) {
    const parts = file.path.split('/')
    let node = root
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i]
      let child = node.dirs.get(seg)
      if (!child) {
        child = { name: seg, path: node.path ? `${node.path}/${seg}` : seg, dirs: new Map(), files: [] }
        node.dirs.set(seg, child)
      }
      node = child
    }
    node.files.push(file)
  }
  return root
}

// Roll-up counts so a collapsed folder can still show how many files need a push.
function countByStatus(node: DirNode): Record<RepoFileStatus, number> {
  const acc: Record<RepoFileStatus, number> = { synced: 0, pending: 0, pull_pending: 0, muted: 0 }
  for (const f of node.files) acc[f.status]++
  for (const child of node.dirs.values()) {
    const sub = countByStatus(child)
    acc.synced += sub.synced
    acc.pending += sub.pending
    acc.pull_pending += sub.pull_pending
    acc.muted += sub.muted
  }
  return acc
}

const fileName = (path: string) => path.slice(path.lastIndexOf('/') + 1)

// A small glyph per file type so the tree scans quickly.
function fileIcon(path: string): string {
  const name = fileName(path).toLowerCase()
  if (name.endsWith('.md')) return '📄'
  if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a')) return '🎵'
  if (/\.(jpe?g|png|gif|webp|svg)$/.test(name)) return '🖼️'
  if (name.endsWith('.yml') || name.endsWith('.yaml')) return '⚙️'
  if (name === '.gitignore') return '🚫'
  return '📄'
}

function Tree({
  node,
  depth,
  collapsed,
  toggle,
}: {
  node: DirNode
  depth: number
  collapsed: Set<string>
  toggle: (path: string) => void
}) {
  const dirs = [...node.dirs.values()].sort((a, b) => a.name.localeCompare(b.name))
  const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path))
  const pad = (n: number) => ({ paddingLeft: `${0.75 + n * 0.85}rem` })

  return (
    <>
      {dirs.map((dir) => {
        const isCollapsed = collapsed.has(dir.path)
        const counts = countByStatus(dir)
        return (
          <div key={dir.path}>
            <button
              type="button"
              onClick={() => toggle(dir.path)}
              style={pad(depth)}
              className="flex w-full items-center gap-1.5 py-1.5 pr-3 text-left text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <span className={`shrink-0 text-[10px] text-slate-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>
                ▶
              </span>
              <span className="shrink-0">{isCollapsed ? '📁' : '📂'}</span>
              <span className="truncate">{dir.name}/</span>
              <span className="ml-auto flex items-center gap-1">
                {counts.pull_pending > 0 && (
                  <span
                    className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-semibold text-white"
                    title={`${counts.pull_pending} not pulled`}
                  >
                    {counts.pull_pending}
                  </span>
                )}
                {counts.pending > 0 && (
                  <span
                    className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white"
                    title={`${counts.pending} not pushed`}
                  >
                    {counts.pending}
                  </span>
                )}
              </span>
            </button>
            {!isCollapsed && (
              <Tree node={dir} depth={depth + 1} collapsed={collapsed} toggle={toggle} />
            )}
          </div>
        )
      })}
      {files.map((f) => (
        <div
          key={f.path}
          style={pad(depth)}
          className={`flex items-center gap-2 py-1.5 pr-3 text-sm ${STATUS_META[f.status].row}`}
          title={STATUS_META[f.status].label}
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_META[f.status].dot}`} />
          <span className="shrink-0 text-xs">{fileIcon(f.path)}</span>
          <span className="truncate font-mono text-xs">{fileName(f.path)}</span>
        </div>
      ))}
    </>
  )
}

export default function RepoPreview({ slug, onClose }: Props) {
  const queryClient = useQueryClient()
  const { data: files, isLoading, error } = useRepoPreview(slug)
  const status = useSyncStatus(slug, true)
  const sync = useSyncBook()

  // undefined = not pushing; string|null = lastSyncedAt snapshot at click time.
  const [pushBaseline, setPushBaseline] = useState<string | null | undefined>(undefined)
  const [justPushed, setJustPushed] = useState(false)

  // Default-collapse nothing; folders start expanded.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(path) ? next.delete(path) : next.add(path)
      return next
    })

  // Ignored files are hidden by default — they're repo noise TransliFlow doesn't manage.
  const [showIgnored, setShowIgnored] = useState(false)

  const pendingCount = (files ?? []).filter((f) => f.status === 'pending').length
  const mutedCount = (files ?? []).filter((f) => f.status === 'muted').length
  const visibleFiles = useMemo(
    () => (showIgnored ? (files ?? []) : (files ?? []).filter((f) => f.status !== 'muted')),
    [files, showIgnored],
  )
  const tree = useMemo(() => buildTree(visibleFiles), [visibleFiles])

  // Detect when the background push lands: lastSyncedAt moves off the baseline.
  useEffect(() => {
    if (pushBaseline === undefined) return
    const current = status.data?.lastSyncedAt ?? null
    if (current === pushBaseline) return
    setPushBaseline(undefined)
    setJustPushed(true)
    queryClient.invalidateQueries({ queryKey: ['repo-preview', slug] })
    const t = setTimeout(() => setJustPushed(false), 3000)
    return () => clearTimeout(t)
  }, [status.data?.lastSyncedAt, pushBaseline, queryClient, slug])

  // Safety net so the spinner can't hang forever if the webhook never lands.
  useEffect(() => {
    if (pushBaseline === undefined) return
    const t = setTimeout(() => setPushBaseline(undefined), 60_000)
    return () => clearTimeout(t)
  }, [pushBaseline])

  const pushing = sync.isPending || pushBaseline !== undefined
  const onSync = () => {
    if (!status.data?.id) return
    setPushBaseline(status.data.lastSyncedAt ?? null)
    sync.mutate(status.data.id, { onError: () => setPushBaseline(undefined) })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Repo files</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          {(Object.keys(STATUS_META) as RepoFileStatus[]).map((s) => (
            <span key={s} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${STATUS_META[s].dot}`} />
              {STATUS_META[s].label}
            </span>
          ))}
          {mutedCount > 0 && (
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 select-none">
              <input
                type="checkbox"
                checked={showIgnored}
                onChange={(e) => setShowIgnored(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300"
              />
              Show ignored files
            </label>
          )}
        </div>

        <div className="mt-3 flex-1 overflow-y-auto rounded-lg border border-slate-200">
          {isLoading && <p className="p-3 text-sm text-slate-400">Loading repo…</p>}
          {error && <p className="p-3 text-sm text-red-600">Couldn't load the repo.</p>}
          {!isLoading && !error && (files?.length ?? 0) === 0 && (
            <p className="p-3 text-sm text-slate-400">No files yet.</p>
          )}
          {!isLoading && !error && visibleFiles.length > 0 && (
            <Tree node={tree} depth={0} collapsed={collapsed} toggle={toggle} />
          )}
        </div>

        {sync.isError && (
          <p className="mt-3 text-xs text-red-600">
            {sync.error instanceof Error ? sync.error.message : 'Push failed.'}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {mutedCount > 0
              ? `${mutedCount} file${mutedCount === 1 ? '' : 's'} in the repo TransliFlow ignores.`
              : 'TransliFlow manages every file in this repo.'}
          </p>
          <button
            onClick={onSync}
            disabled={pushing || !status.data?.id}
            title={pushing ? 'Syncing with GitHub…' : 'Sync with GitHub (pull then push)'}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {pushing ? (
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5 animate-spin">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5 fill-current">
                <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 0 0 5.47 7.59c.4.07.55-.17.55-.38v-1.33c-2.23.48-2.7-1.07-2.7-1.07-.36-.92-.89-1.17-.89-1.17-.73-.5.05-.49.05-.49.8.06 1.22.82 1.22.82.72 1.23 1.88.88 2.34.67.07-.52.28-.88.5-1.08-1.78-.2-3.64-.89-3.64-3.96 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 4 0c1.52-1.03 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.28.82 2.15 0 3.08-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48v2.2c0 .21.15.46.55.38A8 8 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
              </svg>
            )}
            <span>
              {pushing ? 'Syncing…' : justPushed ? 'Synced' : pendingCount > 0 ? `Sync (${pendingCount})` : 'Sync'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
